import t from '@jetshop/intl';
import React, { useState, useContext, useEffect, useMemo } from 'react';
import { useQuery } from 'react-apollo';
import { styled } from 'linaria/react';
import { fuzzySearch } from '@jetshop/ui/fuzzySearch';
import { distance } from '@jetshop/core/helpers/distance';
import slugify from 'slugify';
import MaxWidth from '../Layout/MaxWidth';
import LoadingPage from '../LoadingPage';
import storeLocatorQuery from './StoreLocator.gql';
import StoreList from './StoreList';
import StoreMap from './StoreMap';
import { useLocationState } from './useLocationState';
import StoresDropdown from './StoresDropdown';
import ChannelContext from '@jetshop/core/components/ChannelContext';
import UserLocation from './UserLocation';
import { useParams, useHistory } from 'react-router-dom';
import { theme } from '../Theme';
import { StructuredData } from '@jetshop/core/components/StructuredData';
import { Helmet } from 'react-helmet-async';

const Flex = styled('div')`
  h1 {
    margin-bottom: 0.5em;
  }

  ${theme.below.lg} {
    flex-direction: column;
  }
`;

const Header = styled(Flex)`
  padding: 3rem 0;
  background: #fff;
  text-align: center;

  ${theme.below.lg} {
    padding: 1rem 0;
  }

  h1#store-locator-header,
  h2#store-locator-header {
    font-weight: 600;
    line-height: normal;
    font-size: 30px;
    margin-bottom: 10px;
    padding-bottom: 10px;
    text-transform: uppercase;
}

  p {
    font-size: 16px;
  }
`;

const StoreLocatorWrapper = styled(MaxWidth)`
  margin-bottom: 4rem;
  max-width: 100%;
  padding: 0 !important;
  width: 100%;
  margin-bottom: 0;
  ${theme.below.lg} {
    padding: 0;
  }
`;

const StoreLocatorContent = styled(Flex)`
  ${theme.below.lg} {
    height: unset;
  }
`;

const SearchAndListContainer = styled('div')`
  height: 100%;
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  padding: 2rem;

  ${theme.below.lg} {
    flex-direction: column;
  }

  & > div {
    width: 100%;
    padding: 0;
    margin:0 10px;
    &:first-of-type {
      margin-bottom: 1rem;

      svg {
        max-width: 30px;
        width: 20px;
        height: 20px;
      }
    }
    ${theme.below.lg} {
      margin:0;
    }
  }

  svg {
    max-width: 25px;
  }

  & > div  {
    position: relative;
    width: 100%;
    padding-top: 0;
    padding-bottom: 0;
    }

    ul {
      border-left: 1px solid #000;
      border-right: 1px solid #000;
      border-bottom: 1px solid #000;

      position: absolute;
      max-width: 100%;
      width: 100%;
      z-index: 2;
      li {
        padding: 10px;
        &:hover {
          background-color: #f2f2f2 !important;
        }
        &:focus {
          background-color: #f2f2f2 !important;
        }
      }
    }
  
    input,button {
      width: 100%;
      height: 46px;
      padding: 0 48px 0 10px;
      text-align:left;
      font-weight:600;
      font-size: 16px;
      border-radius: 0;
      outline: none;
      background: #fff;
  
      border: 1px solid #000;
  
      font-family: 'Source Sans Pro', sans-serif;
      &::placeholder {
        color:#000;
        opacity:1;
      }
    }
  
    svg {
      position: absolute;
      top: 15px;
      right: 18px;
  
      stroke: transparent;
      fill: #8a8a8a;
    }
`;

export const LocationStateContext = React.createContext(null);

export const countries = {
  SV: 'Sverige',
  DK: 'Danmark',
  NO: 'Norge',
  FI: 'Suomi',
  EU: null
};
const StoreLocator = () => {
  const { selectedChannel } = useContext(ChannelContext);
  const { data, loading } = useQuery(storeLocatorQuery);
  const history = useHistory();
  const params = useParams();
  const [selectedRegion, selectRegion] = useState(
    countries[selectedChannel.name]
  );
  const locationState = useLocationState();
  const [search, setSearch] = useState('');
  const [userLocation, setUserLocation] = useState({
    latitude: null,
    longitude: null
  });
  const [closestStore, setClosestStore] = useState(null);
  const [distances, setDistances] = useState({});
  const [selectedStore, setSelectedStore] = useState(null);
  const [filteredStores, setFilteredStores] = useState(null);

  const stores = useMemo(() => {
    if (!data?.stores) return [];
    if (search.length !== 0)
      return fuzzySearch({
        data: data?.stores,
        keys: ['name', 'address1'],
        searchTerm: search
      });
    return data?.stores;
  }, [data?.stores, search?.length]);


  const storeListProps = {
    stores,
    userLocation,
    setUserLocation,
    distances,
    setSelectedStore
  };

  useEffect(() => {
    if (data?.stores && selectedStore) {
      const region = slugify(
        selectedStore?.address2?.replace(/(<([^>]+)>)/gi, '').toLowerCase() ??
          ''
      );
      if (
        region &&
        slugify(selectedStore?.name).toLowerCase() !== params?.slug
      ) {
        history.push(
          `/stores/${region}/${slugify(selectedStore?.name).toLowerCase()}`
        );
      } else if (slugify(selectedStore?.name).toLowerCase() !== params?.slug) {
        history.push(`/stores/${slugify(selectedStore?.name).toLowerCase()}`);
      }
    }
  }, [selectedStore]);

  useEffect(() => {
    if (params?.slug && params?.region && data?.stores) {
      const preselected = data.stores.find(
        s =>
          slugify(s.name).toLowerCase() === params.slug &&
          slugify(s?.address2?.replace(/(<([^>]+)>)/gi, '').toLowerCase()) ===
            params.region
      );
      setSelectedStore(preselected);
    } else if (params?.slug && data?.stores) {
      const preselected = data.stores.find(
        s => slugify(s.name).toLowerCase() === params.slug
      );
      if (preselected) {
        setSelectedStore(preselected);
      } else {
        setFilteredStores(
          data.stores.filter(s => {
            return (
              s.address2?.replace(/(<([^>]+)>)/gi, '').toLowerCase() ===
              params.slug
            );
          })
        );
      }
    }
  }, [data?.stores, params?.slug]);

  const isStructuredDataComplete = data => {
    const requiredFields = [
      'name',
      'address1'
    ];
  
    return requiredFields.every(field => {
      const value = data[field];
      // Check if the value exists and is not empty
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return Boolean(value);
    });
  };

  const storeMapProps = {
    stores,
    userLocation,
    closestStore,
    distances,
    selectedRegion,
    setSelectedStore,
    filteredStores
  };
  useEffect(() => {
    if (userLocation?.latitude === null) return;
    if (!data?.stores) return;
    let distances = {};
    let closest = [];
    stores.forEach(store => {
      const other = store.other
        .replace(/<[^>]*>/g, '')
        .replace(/\r?\n|\r/g, '-')
        .split('-');
      const storeCoords = {
        latitude: parseFloat(other[0]),
        longitude: parseFloat(other[1])
      };
      const thisDistance = distance(userLocation, storeCoords);
      distances[store.id] = thisDistance;

      closest.push({ ...store, distance: thisDistance });
    });
    closest.sort((a, b) =>
      a.distance > b.distance ? 1 : b.distance > a.distance ? -1 : 0
    );
    setDistances(distances);
    setClosestStore(closest.slice(0, 5));
  }, [userLocation?.latitude, data?.stores]);

  useEffect(() => {
    const storeLocatorHeader = document.getElementById('store-locator-header');
    const storeDetailHeader = document.getElementById('store-detail-header');
    if (storeLocatorHeader && storeDetailHeader) {
      const h2 = document.createElement('h2');
      h2.id = 'store-locator-header';
      h2.innerHTML = storeLocatorHeader.innerHTML;
      h2.className = storeLocatorHeader.className;
      
      storeLocatorHeader.parentNode.replaceChild(h2, storeLocatorHeader);
    }
  }, [selectedStore]);

  // Structured data for the recipe
  let storeStructuredData = {};
  const getStructuredDataFromStore = (
    name,
    address
  ) => {
    storeStructuredData = {
      '@type': 'Store',
      name: name || '',
      address: address || ''
    };
    return storeStructuredData;
  };

  getStructuredDataFromStore(
    selectedStore?.name,
    selectedStore?.address1
  );

  const structuredDataIsReady = isStructuredDataComplete(storeStructuredData);
  return (
    <>
    {selectedChannel?.name === 'SV' && (
    <Helmet>
        <title>{`Hööks ${selectedStore?.name ?? 'butiker'} - Öppettider och adress`} </title>
        <meta name="description" content={`Besök Hööks ${selectedStore?.name ?? 'butiker'}! Alltid med ett brett sortiment med allt för ryttare, häst och hund. Eller handla online direkt på vår webbplats!`} />
      </Helmet>
      )}
      <Header>
        <MaxWidth>
          <div>
            <h1 id="store-locator-header">{t('Find store')}</h1>
            <p>{t('Choose your store to get open hours and contact info.')}</p>
          </div>
        </MaxWidth>
      </Header>
      <StoreLocatorWrapper>
        <StoreLocatorContent>
          {loading ? (
            <LoadingPage />
          ) : (
            <LocationStateContext.Provider value={locationState}>
              <SearchAndListContainer>
                {/* <StoreSearch {...storeSearchProps} /> */}

                <StoresDropdown
                  selectedStore={selectedStore}
                  setSelectedStore={setSelectedStore}
                  selectRegion={selectRegion}
                  selectedRegion={selectedRegion}
                  {...storeListProps}
                />
                <UserLocation
                  setUserLocation={setUserLocation}
                  setSelectedStore={setSelectedStore}
                />
              </SearchAndListContainer>
              <StoreMap selectedStore={selectedStore} {...storeMapProps} />
              <StoreList {...storeListProps} />
            </LocationStateContext.Provider>
          )}
        </StoreLocatorContent>
      </StoreLocatorWrapper>
      {structuredDataIsReady && (
        <StructuredData
          json={[
            {
              '@context': 'https://schema.org',
              ...storeStructuredData
            }
          ]}
        />
      )}
    </>
  );
};

export default StoreLocator;
